// ============================================================================
//  Cliente de /pruebas — diagnóstico en vivo del portal (lector exterior +
//  interior) para validar en campo que el sistema cuenta ingresos/salidas.
//  Se alimenta de tres fuentes: 'state' (snapshot de 1 s, ya existente),
//  'tracking' (feed nuevo — un evento MQTT ya procesado, ver
//  control.types.ts::TrackingSighting) y /api/events (bitácora persistida).
// ============================================================================
import type {
  StateSnapshot,
  DetectedBeacon,
  InsideBeacon,
  ReaderSummary,
  TransitBeacon,
} from '../../src/shared/types';
import type { TrackingSighting } from '../../src/modules/control/control.types';
import { esc, getJson, toast } from '../shared/api';
import { portalLanes } from '../shared/portal-lanes';

declare const io: (() => { on: (event: string, cb: (data: unknown) => void) => void }) | undefined;

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function fmtAgo(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `${s} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}

// ==========================================================================
//  Cadena de escáneres — una fila por bocamina, los lectores en su orden
//  físico y bajo cada uno los fotochecks que está viendo ahora mismo
//  (ReaderSummary.seen, TTL corto: ver SCANNER_SIGHTING_TTL_SECONDS).
// ==========================================================================
const ZONE_SHORT: Record<string, string> = { exterior: 'ext', middle: 'med', interior: 'int' };

/** Agrupa preservando el orden de aparición de cada clave. */
function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const list = out.get(k);
    if (list) list.push(row);
    else out.set(k, [row]);
  }
  return out;
}

function scannerCard(r: ReaderSummary, snap: StateSnapshot): string {
  const down = !r.online || r.lastSeenSecondsAgo > 90;
  const visto = r.online ? 'en línea' : `hace ${fmtAgo(r.lastSeenSecondsAgo)}`;
  const zoneShort = r.zone ? (ZONE_SHORT[r.zone] ?? r.zone) : '—';
  const body = r.seen.length
    ? r.seen
        .map((s) => {
          const cls = s.subjectType === 'vehicle' ? 'vehicle' : 'person';
          // 'desaparece' es la última vista antes de perderla: el lector ya
          // no la tiene delante, pero sigue contando dentro del TTL corto.
          const fading = s.lastEvent === 'desaparece' ? ' pr-fading' : '';
          return `<div class="pr-seen${fading}">
            <span class="chip ${cls}">${esc(s.name || s.code || s.beaconMac)}</span>
            <span class="pr-ago">${s.rssi ?? '—'} dBm · ${fmtAgo(snap.ts - s.lastSeenAt)}</span>
          </div>`;
        })
        .join('')
    : '<p class="empty">Sin fotochecks a la vista.</p>';
  // el tag se repite entre escáneres de la misma bocamina/segmento, así que
  // la MAC es lo único que identifica al lector en campo.
  return `<div class="pr-scan${down ? ' pr-scan-down' : ''}">
    <div class="pr-scan-h">
      <span class="zbadge ${r.zone ?? 'none'}">${zoneShort} #${r.sortOrder}</span>
      <span class="pr-scan-dot ${down ? 'down' : 'ok'}" title="${visto}"></span>
    </div>
    <div class="pr-scan-tag">${esc(r.tag)}</div>
    <div class="pr-scan-mac mono">${esc(r.mac || '—')}</div>
    <div class="pr-scan-state ${down ? 'down' : ''}">${visto}</div>
    <div class="pr-scan-body">${body}</div>
  </div>`;
}

function renderChain(snap: StateSnapshot): void {
  const el = $('pr-chain');
  if (!el) return;
  if (!snap.readers.length) {
    el.innerHTML =
      '<div class="empty">Sin lectores configurados — revisa los escáneres en <a href="/config">Configuración</a>.</div>';
    return;
  }

  const byArea = groupBy(snap.readers, (r) => r.area);
  // las bocaminas primero, y al final el cajón de los escáneres que el
  // operador todavía no asignó (area === '')
  const areas = [...byArea.keys()].sort((a, b) =>
    a === '' ? 1 : b === '' ? -1 : a.localeCompare(b),
  );

  el.innerHTML = areas
    .map((area) => {
      // sortOrder 1 = el más interno: descendente pinta exterior → interior,
      // que es como se recorre el túnel al entrar.
      const readers = [...(byArea.get(area) ?? [])].sort((a, b) => b.sortOrder - a.sortOrder);
      const counts = snap.areas?.[area];
      const head = area
        ? `<b>${esc(area)}</b> <span class="kv">${counts?.persons ?? 0} p · ${counts?.vehicles ?? 0} v dentro</span>`
        : '<b>Sin bocamina asignada</b> <span class="kv">no participan en presencia — asígnalos en <a href="/config">Configuración</a></span>';
      return `<div class="pr-chain-portal">
        <div class="pr-chain-h">${head}</div>
        <div class="pr-chain-row">${readers.map((r) => scannerCard(r, snap)).join('')}</div>
      </div>`;
    })
    .join('');
}

// ==========================================================================
//  Ocupación
// ==========================================================================
function renderOcc(snap: StateSnapshot): void {
  const p = $('pr-persons');
  if (p) p.textContent = String(snap.persons ?? 0);
  const v = $('pr-vehicles');
  if (v) v.textContent = String(snap.vehicles ?? 0);
  const areaNames = Object.keys(snap.areas || {});
  const el = $('pr-areas');
  if (el) {
    el.innerHTML = areaNames
      .sort()
      .map((n) => {
        const a = snap.areas[n];
        return `<div class="fanmap-row"><b>${esc(n || '(global)')}</b> — ${a?.persons ?? 0} p · ${a?.vehicles ?? 0} v</div>`;
      })
      .join('');
  }

  const transitEl = $('pr-transit');
  if (transitEl) {
    const transit = snap.transit ?? [];
    if (!transit.length) {
      transitEl.innerHTML = '';
    } else {
      const entrando = transit.filter((t) => t.direction === 'in').length;
      const saliendo = transit.filter((t) => t.direction === 'out').length;
      transitEl.innerHTML = `<div class="fanmap-row"><b>En tránsito</b> — ${entrando} entrando · ${saliendo} saliendo</div>`;
    }
  }
}

// ==========================================================================
//  Mapa del portal — cuatro carriles por bocamina: Fuera / Entrando ↓ /
//  Saliendo ↑ / Dentro. Una baliza aparece en un solo carril, el más avanzado
//  que le corresponda: Saliendo > Dentro > Entrando > Fuera. Ojo con el primero
//  — quien sale sigue figurando DENTRO hasta que el exterior lo confirme, y
//  darle prioridad a `inside` dejaba el carril Saliendo vacío para siempre.
//  El reparto y su porqué están en client/shared/portal-lanes.ts.
// ==========================================================================
interface MapChip {
  label: string;
  ago: string;
  warn: boolean; // vio el lector interior sin haber pasado por el exterior
  subjectType: string;
}

function who(b: { name?: string; code: string; beaconMac: string }): string {
  return b.name || b.code || b.beaconMac;
}

function mapChip(c: MapChip): string {
  const cls = c.subjectType === 'vehicle' ? 'vehicle' : 'person';
  return `<div class="pr-chip">
    <span><span class="chip ${cls}">${esc(c.label)}</span>${c.warn ? '<span class="pr-warn-noentry">⚠ sin ingreso</span>' : ''}</span>
    <span class="pr-ago">${c.ago}</span>
  </div>`;
}

function lane(title: string, cls: string, chips: MapChip[], vacio: string, hint = ''): string {
  return `<div class="pr-lane">
    <div class="pr-lane-h ${cls}">${title}</div>
    ${hint ? `<p class="pr-col-hint">${hint}</p>` : ''}
    ${chips.length ? chips.map(mapChip).join('') : `<p class="empty">${vacio}</p>`}
  </div>`;
}

function renderMap(snap: StateSnapshot): void {
  const el = $('pr-map');
  if (!el) return;

  // el reparto en carriles vive en client/shared/portal-lanes.ts, puro y con
  // su propio spec: ahí estuvo escondido el bug del carril Saliendo ↑
  const { dentro, enTransito, fuera } = portalLanes(snap);

  const areas = [...new Set([...dentro.keys(), ...enTransito.keys(), ...fuera.keys()])].sort(
    (a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)),
  );
  if (!areas.length) {
    el.innerHTML =
      '<p class="empty">Ninguna baliza a la vista — acerca un fotocheck al lector.</p>';
    return;
  }

  const toChipInside = (b: InsideBeacon): MapChip => ({
    label: who(b),
    ago: `dentro hace ${fmtAgo(snap.ts - b.sinceAt)}`,
    warn: false,
    subjectType: b.subjectType,
  });
  const toChipTransit = (t: TransitBeacon): MapChip => ({
    label: who(t),
    ago: `hace ${fmtAgo(snap.ts - t.sinceAt)}`,
    warn: false,
    subjectType: t.subjectType,
  });
  // "sin ingreso": la vio primero el lector interior, así que está dentro del
  // alcance del interior pero el sistema no la cuenta hasta que pase por el
  // exterior — el caso que más confunde en campo, por eso se marca.
  const toChipOut = (d: DetectedBeacon): MapChip => ({
    label: who(d),
    ago: `hace ${fmtAgo(snap.ts - d.lastSeenAt)}`,
    warn: d.zone === 'interior',
    subjectType: d.subjectType,
  });

  el.innerHTML = areas
    .map((area) => {
      const transit = enTransito.get(area) ?? [];
      const lanes = [
        lane(
          'Fuera',
          '',
          (fuera.get(area) ?? []).map(toChipOut),
          'Ninguna baliza fuera.',
          'Vista por algún lector, sin ingreso confirmado.',
        ),
        lane(
          'Entrando ↓',
          'pr-lane-h-in',
          transit.filter((t) => t.direction === 'in').map(toChipTransit),
          'Nadie entrando.',
        ),
        lane(
          'Saliendo ↑',
          'pr-lane-h-out',
          transit.filter((t) => t.direction === 'out').map(toChipTransit),
          'Nadie saliendo.',
        ),
        lane('Dentro', 'pr-lane-h-ok', (dentro.get(area) ?? []).map(toChipInside), 'Nadie dentro.'),
      ].join('');
      const head = area ? esc(area) : 'Sin bocamina asignada';
      return `<div class="pr-map-portal">
        <div class="pr-chain-h"><b>${head}</b></div>
        <div class="pr-lanes">${lanes}</div>
      </div>`;
    })
    .join('');
}

// ==========================================================================
//  Feed en vivo — últimas ~100 TrackingSighting, más reciente arriba
// ==========================================================================
const FEED_MAX = 100;
let feedRows: TrackingSighting[] = [];

const EFF_LABEL: Record<TrackingSighting['effect'], string> = {
  entry: 'ingreso',
  exit: 'salida',
  located: 'ubicado (sin ingreso)',
  heartbeat: 'latido',
  ignored: 'sin cambio',
  'pending-in': 'entrando (pendiente)',
  'pending-out': 'saliendo (pendiente)',
};

function renderFeed(): void {
  const body = $('pr-feed');
  const empty = $('pr-feed-empty');
  if (!body) return;
  if (!feedRows.length) {
    body.innerHTML = '';
    if (empty) empty.style.display = 'block';
    return;
  }
  if (empty) empty.style.display = 'none';
  body.innerHTML = feedRows
    .map((s) => {
      const time = new Date(s.ts * 1000).toLocaleTimeString('es-PE', { hour12: false });
      const skew = Math.abs(s.clockSkewSeconds);
      const skewHtml =
        skew > 60 ? `<span class="pr-skew">desfase ${Math.round(s.clockSkewSeconds)}s</span>` : '';
      const zoneClass = s.zone ?? 'none';
      const zoneShort = s.zone ? (ZONE_SHORT[s.zone] ?? s.zone) : '—';
      const orderLabel = s.order !== null && s.order !== undefined ? ` #${s.order}` : '';
      return `<tr>
        <td>${time}</td>
        <td class="mono">${esc(s.scannerMac || s.scannerId || '—')}</td>
        <td><span class="zbadge ${zoneClass}">${esc(s.area || '—')} · ${zoneShort}${orderLabel}</span></td>
        <td>${esc(s.ev)}</td>
        <td class="mono">${esc(s.code || s.beaconMac)}</td>
        <td>${s.rssi ?? '—'} dBm</td>
        <td><span class="pr-eff pr-eff-${s.effect}">${EFF_LABEL[s.effect]}</span>${s.corrected ? ' <span class="chip chip-warn">corregido</span>' : ''}</td>
        <td>${esc(s.note)} ${skewHtml}</td>
      </tr>`;
    })
    .join('');
}

// ==========================================================================
//  Bitácora persistida — /api/events?limit=50, cada 8 s
// ==========================================================================
interface EventRow {
  id: number;
  ts: number;
  type: string;
  beaconMac: string;
  subjectType: string;
  detail: string;
}

function loadLog(): void {
  getJson<EventRow[]>('/api/events?limit=50')
    .then((rows) => {
      const el = $('pr-log');
      if (!el) return;
      if (!rows.length) {
        el.innerHTML = '<div class="empty">Sin eventos todavía.</div>';
        return;
      }
      el.innerHTML = rows
        .map((r) => {
          const time = new Date(r.ts * 1000).toLocaleTimeString('es-PE', { hour12: false });
          return `<div class="evt"><span class="k ${esc(r.type)}">${esc(r.type)}</span>
            <span>${time}</span> <span class="mono">${esc(r.beaconMac || '—')}</span>
            <span>${esc(r.detail)}</span></div>`;
        })
        .join('');
    })
    .catch(() => undefined);
}

// ==========================================================================
//  Arranque
// ==========================================================================
if (typeof io === 'function') {
  const socket = io();
  socket.on('state', (data) => {
    const snap = data as StateSnapshot;
    renderChain(snap);
    renderOcc(snap);
    renderMap(snap);
  });
  socket.on('tracking', (data) => {
    feedRows.unshift(data as TrackingSighting);
    if (feedRows.length > FEED_MAX) feedRows = feedRows.slice(0, FEED_MAX);
    renderFeed();
  });
} else {
  toast('socket.io no disponible: ¿se cargó /socket.io/socket.io.js?', true);
}

loadLog();
setInterval(loadLog, 8000);
